import { castArray, isNil, mapKeys } from 'lodash-es';
import { ErrorResponse, UserManagerSettings } from 'oidc-client-ts';

import { services } from 'services/base';
import {
  AuthAudience,
  AuthManagerConfig,
  AuthManagerInterface,
  AuthMode,
  AuthRight,
  UnsubscribeMethod,
  User,
  UserProfile,
} from 'types';
import { decodeTokenPayload, getOfflineUser, logger } from 'utils';

import {
  ACCESS_TOKEN_EXPIRING_NOTIFICATION_TIME_IN_SECONDS,
  convertConfigToUserManagerSettings,
  getError,
  getMockUser,
  removeOidcParams,
  setupOidcLogLevel,
  shouldCallRedirectCallback,
} from './helpers';
import UserManager from './UserManager';

const emptyArray = [];

const popupLoginRequiredErrors = ['login_required', 'interaction_required'];

const sessionExpirationErrors = [
  'invalid_grant',
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
];

const RENEWAL_TIMEOUT = 30000;
type TokenRenewalState = {
  tries: number;
  timerId: NodeJS.Timeout | null;
  promise: Promise<User | null> | null;
  resolve: null | ((value: User | null) => void);
  reject: null | ((reason?: unknown) => void);
};

export const ACTION_ROOT_USER_CHANGE = 'AppFrame:Auth:UserChange:$root$';

/**
 * A class representing an OIDC client.
 *
 * ### Multiple client solution
 *
 * AppFrame can support multiple OIDC clients. Each client can be configured with its own OIDC settings.
 * The root client is the main OIDC client that is used for authentication and authorization. It can be configured
 * under the `oidc` property in the main configuration.
 * All other clients are considered module-specific OIDC clients. They can be configured under the `oidc` property
 * of the module configuration.
 *
 * ### Authentication modes
 *
 * AppFrame can be configured to use one of the following authentication modes. See `AuthMode` enum for further
 * details.
 */
class AuthManager implements AuthManagerInterface {
  private config: AuthManagerConfig;

  private userManagerSettings: UserManagerSettings;

  private online: boolean = false;

  private moduleId: string | null = null;

  private actionUserChange: string;

  private renewalState: TokenRenewalState | null = null;

  private unsubscribeOnlineChange: UnsubscribeMethod | null = null;

  /**
   * The OIDC user manager instance.
   */
  private manager: UserManager | null = null;

  /**
   * The actual OIDC user object.
   */
  private user: User | null = null;

  public getUser = () => this.user;

  public onUserChange = (callback) => {
    services.registerAction(this.actionUserChange, callback, { order: 1 });
    return () => services.clearAction(this.actionUserChange, callback);
  };

  private rights: AuthRight[] = [];

  private rightsMap: Record<AuthRight, true> = {};

  public getRights = () => this.rights;

  public getRightsMap = () => this.rightsMap;

  private audiences: AuthAudience[] = [];

  private audiencesMap: Record<AuthAudience, true> = {};

  public getAudiences = () => this.audiences;

  public getAudiencesMap = () => this.audiencesMap;

  public getAccessToken = () => this.user?.access_token || null;

  public getAccessTokenAsync = async () => {
    const { user, manager } = this;
    if (!user || !this.online || !AuthManager.checkManager(manager)) {
      return null;
    }

    const asyncTokenRenewTimeout =
      this.config.accessTokenExpiringNotificationTimeInSeconds || ACCESS_TOKEN_EXPIRING_NOTIFICATION_TIME_IN_SECONDS;
    if ((user.expires_in ?? -1) < asyncTokenRenewTimeout) {
      logger.info(`Token will expire in less than ${asyncTokenRenewTimeout} seconds: renewing silently...`);
      const newUser = await manager.signinSilent();
      this.updateUser(newUser);
      return newUser?.access_token ?? null;
    }

    return user.access_token ?? null;
  };

  private profile: UserProfile | null = null;

  public getProfile = () => this.profile;

  private updateUser = (user: User | null) => {
    if (user === this.user) {
      return;
    }

    services.executeAction(this.actionUserChange, user);
  };

  private handleUserChange = (user: User | null) => {
    this.user = user;
    services.appFrame.offline.updateOfflineUser(user, this.moduleId);
    if (!user) {
      this.profile = null;
      this.rights = emptyArray;
      this.rightsMap = {};
      this.audiences = emptyArray;
      this.audiencesMap = {};
      return;
    }

    const { profile: userProfile, access_token: token } = user;
    const rights = isNil(userProfile.rights) ? [] : castArray(userProfile.rights);
    this.profile = { ...userProfile, rights };

    this.rights = (rights || emptyArray) as never[];
    this.rightsMap = mapKeys(rights);

    const jwtToken = decodeTokenPayload(token);
    this.audiences = castArray(jwtToken?.aud || emptyArray) as never[];
    this.audiencesMap = mapKeys(this.audiences);
  };

  private handleUserLoaded = (user: User) => {
    this.updateUser(user);
  };

  private stopTokenRenewal = () => {
    const renewalState = this.renewalState;
    if (!renewalState) {
      return;
    }

    if (renewalState.timerId) {
      clearTimeout(renewalState.timerId);
    }

    this.renewalState = null;
  };

  private renewTokenInternal = () => {
    if (!this.online || !AuthManager.checkManager(this.manager)) {
      this.stopTokenRenewal();
      return;
    }

    const { renewalState } = this;
    if (!renewalState) {
      logger.warn('Token renewal requested in invalid state!');
      return;
    }

    renewalState.tries += 1;
    logger.info(`Renewing token silently (try: ${renewalState.tries})...`);
    this.manager
      .signinSilent()
      .then((user) => {
        // do not update anything when token renewal was re-initiated while waiting for the previous one
        if (this.renewalState !== renewalState) {
          logger.warn('Token renewal had been re-initiated while waiting for the previous one.');
          this.stopTokenRenewal();
          return;
        }

        logger.info('Token renewal was successful.');
        this.updateUser(user);
        this.stopTokenRenewal();
        renewalState.resolve?.(user);
      })
      .catch((error: ErrorResponse) => {
        // do not update anything when token renewal was re-initiated while waiting for the previous one
        if (this.renewalState !== renewalState) {
          logger.error(
            `Token renewal was failed, but it had been re-initiated while waiting for the previous one: ${getError(error)}!`,
          );
          return;
        }

        // reject promise to upper-layer
        if (error.error && sessionExpirationErrors.includes(error.error)) {
          logger.info(`Access token renewal failed with error: ${getError(error)} - session expired.`);
          services.appFrame.auth.setSessionExpired(true);
          renewalState.reject?.(error);
          this.destroy(true); // will stop renewal too
          return;
        }

        // retry token renewal
        logger.warn(`Access token renewal failed with error: ${getError(error)} - retrying in ${RENEWAL_TIMEOUT}ms...`);
        this.renewalState.timerId = setTimeout(() => {
          this.renewTokenInternal();
        }, RENEWAL_TIMEOUT);
      });
  };

  private renewToken = async () => {
    try {
      if (!this.online || !AuthManager.checkManager(this.manager)) {
        return this.renewalState?.promise ?? null;
      }

      logger.info(`Access token renewal requested from session...`);
      if (this.renewalState) {
        logger.warn('Token renewal re-requested while waiting for retry - ignoring request!');
        return this.renewalState.promise;
      }

      // check if token renewal is on the way
      const renewalState: TokenRenewalState = {
        tries: 0,
        timerId: null,
        promise: null,
        resolve: null,
        reject: null,
      };
      this.renewalState = renewalState;
      logger.info(`Trying to gain access token...`);

      renewalState.promise = new Promise((resolve, reject) => {
        renewalState.resolve = resolve;
        renewalState.reject = reject;
        this.renewTokenInternal();
      });

      return renewalState.promise;
    } catch (ex: unknown) {
      const error = ex as ErrorResponse;
      logger.error(`Access token expired and silent sign-in not possible: ${getError(error)}!`);
      services.appFrame.auth.setSessionExpired(true);
      this.destroy(true);
      return null;
    }
  };

  private handleAccessTokenExpired = async () => {
    logger.info('Access token is expired, trying with token renewal...');
    await this.renewToken();
  };

  private handleSilentRenewError = async (error: ErrorResponse) => {
    if (error.error && sessionExpirationErrors.includes(error.error)) {
      logger.info(`Silent token renewal was unsuccessful, which triggered a session expiration: ${getError(error)}!`);
      services.appFrame.auth.setSessionExpired(true);
      this.destroy(true);
      return;
    }
    logger.warn('Silent token renewal was unsuccesful, token will expire soon...');
  };

  constructor(config: AuthManagerConfig, moduleId?: string | null) {
    if (!moduleId) {
      setupOidcLogLevel(config.logLevel);
    }

    this.userManagerSettings = convertConfigToUserManagerSettings(config);
    this.config = { ...config, ...this.userManagerSettings };
    this.handleUserChange = this.handleUserChange.bind(this);
    this.handleUserLoaded = this.handleUserLoaded.bind(this);
    this.handleAccessTokenExpired = this.handleAccessTokenExpired.bind(this);
    this.handleSilentRenewError = this.handleSilentRenewError.bind(this);

    this.moduleId = moduleId || null;
    this.actionUserChange = moduleId ? `AppFrame:Auth:UserChange:${moduleId}` : ACTION_ROOT_USER_CHANGE;

    services.registerAction(this.actionUserChange, this.handleUserChange, { order: 0 });
  }

  public login = async (onlySilent?: boolean) => {
    if (!this.online || !AuthManager.checkManager(this.manager)) {
      return this.getUser();
    }

    const user = getMockUser(this.config) || (await this.manager.getUser());
    if (user && !user.expired) {
      this.updateUser(user);
      return user;
    }

    if (this.moduleId || onlySilent) {
      try {
        const newUser = await this.manager.signinSilent();
        return newUser;
      } catch (ex: unknown) {
        const error = ex as ErrorResponse;
        // in case of `invalid_grant` clear tokens from `sessionStorage` and try to restore token from session storage
        if (error.error === 'invalid_grant') {
          Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith(`oidc.user:${this.userManagerSettings.authority}:`)) {
              sessionStorage.removeItem(key);
            }
          });
          const newUser = await this.login(onlySilent);
          return newUser;
        }
        if (onlySilent) {
          return null;
        }
        if (error.error && popupLoginRequiredErrors.includes(error.error)) {
          logger.warn(`Silent login is not availalbe: OIDC returned ${getError(error)}' - reverting to popup login!`);
          return this.manager.signinPopup();
        }
        throw ex;
      }
    }

    await this.manager.signinRedirect();
    return null;
  };

  public internalInit = async (startup: boolean) => {
    if (!AuthManager.checkManager(this.manager)) {
      return;
    }
    const authMode = services.appFrame.auth.getAuthMode();
    if (authMode === AuthMode.NoLogin) {
      return;
    }

    const autoSigninRedirect = !this.moduleId;
    if (autoSigninRedirect && shouldCallRedirectCallback(window.location.hash)) {
      try {
        await this.manager.signinRedirectCallback();
        window.location.hash = removeOidcParams(window.location.hash);
        return;
      } catch (ex: unknown) {
        const err = ex as Error;
        if (/No matching state/.test(err.message)) {
          window.location.hash = removeOidcParams(window.location.hash);
          window.location.reload();
          return;
        }
        logger.error('Error occurred during signinRedirectCallback!', err);
        throw err;
      }
    }

    await this.login(startup ? authMode === AuthMode.Anonymous : true);
  };

  public initialize = async () => {
    const handleOnlineChange = async (online: boolean, startup: boolean = false) => {
      this.online = online;
      if (online) {
        this.manager = new UserManager(this.userManagerSettings);
        this.manager.events.addUserLoaded(this.handleUserLoaded);
        this.manager.events.addAccessTokenExpired(this.handleAccessTokenExpired);
        this.manager.events.addSilentRenewError(this.handleSilentRenewError);
        return this.internalInit(startup);
      }
      // check for offline operation (in offline state this.manager is null)
      const offlineUser = (await getOfflineUser(this.moduleId)) || null;
      this.updateUser(offlineUser);
      return this.destroyManager();
    };

    this.unsubscribeOnlineChange = services.appFrame.offline.onOnlineChange((online) =>
      handleOnlineChange(online, false),
    );

    return handleOnlineChange(services.appFrame.offline.isOnline(), true);
  };

  private destroyManager = async () => {
    const { manager } = this;
    if (manager) {
      manager.events.removeSilentRenewError(this.handleSilentRenewError);
      manager.events.removeAccessTokenExpired(this.handleAccessTokenExpired);
      manager.events.removeUserLoaded(this.handleUserLoaded);
      manager.stopSilentRenew();
    }
  };

  public destroy = async (silentLogout?: boolean) => {
    if (this.unsubscribeOnlineChange) {
      this.unsubscribeOnlineChange();
    }

    this.stopTokenRenewal();

    services.clearAction(this.actionUserChange, true);

    const { manager } = this;
    if (manager) {
      const user = this.getUser();
      try {
        await manager.removeUser();
      } catch (ex) {
        logger.warn(`User removal unsuccessful for ${this.moduleId || 'root'} auth manager!`, ex);
      }
      // logout from root client
      if (!this.moduleId) {
        const idToken = user?.id_token ?? manager.getSignInResponse()?.id_token;
        const signoutOptions = idToken ? { id_token_hint: idToken } : {};
        try {
          await (silentLogout ? manager.signoutSilent() : manager.signoutRedirect(signoutOptions));
        } catch (ex: unknown) {
          const error = ex as ErrorResponse;
          if (error.error) {
            logger.warn(`Signout failed while destroing AuthManager: ${getError(error)}!`);
          }
        }
      }
    }
    await this.destroyManager();
  };

  public getUserManager = () => this.manager;

  public getConfig = (): Readonly<AuthManagerConfig> => this.config;

  public isOnline = () => this.online;

  static checkManager = (manager: UserManager | null): manager is UserManager => {
    if (manager) {
      return true;
    }
    logger.warn('No `manager` defined in authentication manager!');
    return false;
  };
}

export default AuthManager;
